import {
  Alert,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { userService } from "../../../services/userService";
import { authStore } from "../../../stores/authStore";
import { TrashIcon } from "../icons";

const DeleteAccountSection = () => {
  const { t } = useTranslation(['settings']);
  const theme = useTheme();
  const { user } = useAuth();
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteAccount = async () => {
    if (!user?.id) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await userService.deleteAccount(user.id);
      // Account deleted successfully, logout and redirect
      authStore.logout();
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(error instanceof Error ? error.message : t('delete_account_error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAccountClick = () => {
    setDeleteAccountDialogOpen(true);
  };

  const handleDeleteAccountDialogClose = () => {
    setDeleteAccountDialogOpen(false);
    setDeleteError(null);
  };

  return (
    <>
      <ButtonBase
        onClick={handleDeleteAccountClick}
        sx={{
          p: 2,
          gap: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          fontWeight: 400,
          textAlign: 'left',
          '&:hover': {
            backgroundColor: theme.palette.error.light + '10',
          },
        }}
      >
        <TrashIcon
          style={{
            fontSize: 24,
            color: theme.palette.error.main
          }}
        />

        <Typography color="error">{t('delete_account')}</Typography>
      </ButtonBase>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteAccountDialogOpen}
        onClose={handleDeleteAccountDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('delete_account_confirm_title')}
        </DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography>
            {t('delete_account_confirm_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              px: 4
            }}
            variant="outlined"
            onClick={handleDeleteAccountDialogClose}
            disabled={isDeleting}
          >
            {t('delete_account_cancel_button')}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? '...' : t('delete_account_confirm_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteAccountSection; 